// ============================================================================
// Hooks TanStack Query — RESULTATS FINANCIERS (E2) : sommaire / annee / detail
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (`viewer` renvoye par l'API — jamais recalcule au callsite, lecon GA-05) : le SPA
// consomme viewer.canViewDetail pour l'affordance F-504, et /detail re-verifie.
// LECTURE seule : aucune mutation (import CSV + saisie = frag E2b).
//
// Les DTO de lignes sont typees par la LIB DEJA PORTEE (FinanceLineRow de
// src/lib/finance/types) — c'est elle qui alimente computeStatement : le contrat
// serveur est le type de la lib, pas une redeclaration locale.

import { useQuery } from '@tanstack/react-query';
import { api, buildQueryString } from '@gs/api-client';
import type { FinanceLineRow } from '../lib/finance/types';

export type GestFinanceRole = 'admin' | 'manager' | 'director' | 'employee';
export type FinanceView = 'visuels' | 'tableau';

interface Amounts {
  actual: number;
  budget: number;
  priorYear: number;
}

/** Totaux du mois + marge brute (ratio 0..1, M-504) + derniere MAJ. */
export interface FinanceSummary {
  totalRevenue: Amounts;
  totalExpense: Amounts;
  netIncome: Amounts;
  grossMargin: Amounts;
  lastUpdatedAt: string | null;
}

/** M-507 : series « reel » sur 6 mois, du plus ancien au plus recent. */
export interface FinanceTrend {
  periods: string[];
  revenue: number[];
  expense: number[];
  net: number[];
  margin: number[];
}

/** M-508 : un point par mois ; isForecast = mois posterieur a la periode (budget). */
export interface FinanceYearPoint {
  period: string;
  isForecast: boolean;
  revenue: Amounts;
  expense: Amounts;
  net: Amounts;
}

export interface FinanceSummaryResponse {
  period: string;
  lines: FinanceLineRow[];
  summary: FinanceSummary;
  /** canViewDetail = affordance F-504 (« Voir le detail complet »), decidee SERVEUR. */
  viewer: { role: GestFinanceRole; canViewDetail: boolean };
}

export interface FinanceYearResponse {
  period: string;
  points: FinanceYearPoint[];
}

export interface FinanceDetailResponse {
  period: string;
  view: FinanceView;
  lines: FinanceLineRow[];
  summary: FinanceSummary;
  /** null en vue « tableau » : la tendance n'est pas lue (M-507). */
  trend: FinanceTrend | null;
  viewer: { role: GestFinanceRole };
}

const KEY = ['gest', 'finance'] as const;

/**
 * F-503 : sommaire du mois. `period` = param d'URL brut (deep-link F-507) — une
 * valeur absente ou malformee n'est PAS une erreur : le serveur replie (M-506) et
 * renvoie la periode RETENUE, qui pilote l'affichage.
 */
export function useFinanceSummary(period: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'summary', period ?? 'auto'] as const,
    queryFn: () => api.get<FinanceSummaryResponse>(`/api/finance/summary${buildQueryString({ period })}`),
    enabled,
  });
}

/** F-522 : serie annuelle du graphique d'evolution (sommaire). */
export function useFinanceYear(period: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'year', period ?? 'auto'] as const,
    queryFn: () => api.get<FinanceYearResponse>(`/api/finance/year${buildQueryString({ period })}`),
    enabled,
  });
}

/**
 * F-500/F-501 : detail admin. `view` fait partie de la cle : basculer Visuels →
 * Tableau ne rejoue pas la tendance (M-507 — la reponse `tableau` a trend: null).
 * 403 (non-admin) = etat STABLE -> pas de retry.
 */
export function useFinanceDetail(period: string | null, view: FinanceView, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'detail', period ?? 'auto', view] as const,
    queryFn: () => api.get<FinanceDetailResponse>(`/api/finance/detail${buildQueryString({ period, view })}`),
    enabled,
    retry: false,
  });
}
